// confirm() replaces window.confirm (spec §2):
//   if (await confirm({ title, message, confirmLabel, tone: 'danger' })) { … }
// It resolves true on confirm and false on cancel, Escape or an outside click. The shell
// mounts ONE <ConfirmHost> (Shell.jsx). confirm() NEVER throws: bad options are a rejected
// promise, and with no host mounted yet (a module's first-mount effect runs before the
// host registers) the request waits in the queue and the host drains it on registration.
// If no host shows up within a second, one dev warning says so.
//
// No React Aria here: the shell imports this file (through ConfirmHost.jsx) and must
// keep React Aria's overlay code out of its main bundle.
import { createConfirmQueue } from './confirm-queue.js';
import { devWarn } from '../dev-warn.js';

export const confirmQueue = createConfirmQueue();

const TONES = ['neutral', 'danger'];
let warnTimer = null;
let hosts = 0;
// The last host leaving cancels what is pending, but not at once: React StrictMode (dev)
// simulates unmount + remount of an effect in the same tick, and that must not cancel a
// request made before the host registered. A re-register in the same tick revokes it.
let cancelScheduled = false;

export function registerConfirmHost() {
  hosts += 1;
  cancelScheduled = false;
  if (warnTimer !== null) { clearTimeout(warnTimer); warnTimer = null; }
  let registered = true;
  return () => {
    if (!registered) return;
    registered = false;
    hosts -= 1;
    if (hosts === 0) {
      cancelScheduled = true;
      queueMicrotask(() => {
        if (!cancelScheduled || hosts !== 0) return;
        cancelScheduled = false;
        confirmQueue.cancelAll();
      });
    }
  };
}

export function normalizeConfirm({ title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', tone = 'neutral' } = {}) {
  if (!title) throw new Error('@platform/ui: confirm() needs a title');
  if (!TONES.includes(tone)) throw new Error(`@platform/ui: unknown confirm tone '${tone}' (one of ${TONES.join(', ')})`);
  return { title, message, confirmLabel, cancelLabel, tone };
}

export const NO_HOST_WARN_MS = 1000;

/** The warning for requests still waiting with no host, or null. Pure; never names a title. */
export function noHostWarning({ hosts: hostCount, pending }) {
  if (hostCount > 0 || pending === 0) return null;
  return `confirm() is waiting for a <ConfirmHost> (the shell mounts one in Shell.jsx); ${pending} request(s) are queued`;
}

function scheduleNoHostWarning() {
  if (warnTimer !== null) return;
  warnTimer = setTimeout(() => {
    warnTimer = null;
    devWarn(noHostWarning({ hosts, pending: confirmQueue.size() }));
  }, NO_HOST_WARN_MS);
  warnTimer.unref?.();
}

export function confirm(options) {
  let request;
  try {
    request = normalizeConfirm(options);
  } catch (err) {
    return Promise.reject(err);
  }
  const answer = confirmQueue.enqueue(request);
  if (hosts === 0) scheduleNoHostWarning();
  return answer;
}
